import type {
  GetState,
  Dispatch,
  ThunkAction,
  ItemDetail,
} from '@shopee/flow-types';
import type {
  AddToCartItem,
  AddToCartResponse,
  RemoveCartItem,
  AddItemToCartParams,
  AddItemToCartBatchParams,
} from './types';

import type { ApiResponse } from './api';

import { createRequestTypes } from '@shopee/redux-commons';
import * as cartApi from './api';
import callApi from '@shopee/create-api-request';
import {
  fetchItemDetailCached,
  getItemById as getItemByIdDefault,
} from '@shopee/item-redux';
import { fetchCartDrawerItems } from '@shopee/cart-drawer-redux-legacy/src/actions';
import ITEM_MODEL_STATUS from '@shopee/item-model-status';
import getIn from '@shopee/get-in';
import { shouldUsePopupWhenAddToCartError } from './utils';

// MPQ
import { shouldShowMPQ } from '@shopee-dl/minimum-purchase-quantity';
import { CartClientId } from '.';

export const ADD_ITEM_TO_CART = createRequestTypes('ADD_ITEM_TO_CART');

export const ADD_ITEM_TO_CART_BATCH = createRequestTypes(
  'ADD_ITEM_TO_CART_BATCH'
);
export const REMOVE_ITEM_FROM_CART = createRequestTypes(
  'REMOVE_ITEM_FROM_CART'
);
export const REMOVE_QUANTITY_ITEM_FROM_CART = createRequestTypes(
  'REMOVE_QUANTITY_ITEM_FROM_CART'
);

export function removeItemFromCart({
  shopId,
  itemId,
  modelId,
  // add-on-deal
  add_on_deal_id,
  item_group_id,
}: RemoveCartItem) {
  return async (dispatch: Dispatch, getState: GetState) => {
    return await callApi(
      {
        apiCall: () => {
          return cartApi.removeItemFromCart({
            shopId,
            itemId,
            modelId,
            add_on_deal_id,
            item_group_id,
          });
        },
        actions: [
          {
            type: REMOVE_ITEM_FROM_CART.REQUESTED,
          },
          {
            type: REMOVE_ITEM_FROM_CART.SUCCESS,
            payload: (state, action, res) => {
              return {
                ...res,
                // `item` is needed for voucher spend calculation in VLP
                // see pc/app/PageVoucherLanding/components/MinSpendBar/shoppingCartSelection/reducer.js
                item: {
                  shopId,
                  itemId,
                  modelId,
                  add_on_deal_id,
                  item_group_id,
                },
              };
            },
          },
          {
            type: REMOVE_ITEM_FROM_CART.FAILED,
            payload: (state, action, res) => {
              return {
                error: res.error,
              };
            },
          },
        ],
      },
      dispatch,
      getState
    );
  };
}

export function addItemToCart({
  shopId,
  itemId,
  virtualSkuInfo,
  quantity,
  modelId,
  checkout = true,
  update_checkout_only = false,
  donot_add_quantity = false,
  source = '',
  onSuccess,
  done,
  // add-on-deal
  add_on_deal_id,
  item_group_id,
  is_add_on_sub_item,
  client_source,
  showToastAfterSuccess = true,
  cart_client_id,
}: AddItemToCartParams) {
  return async (dispatch: Dispatch, getState: GetState) => {
    const options = cart_client_id ? { cart_client_id } : {};
    return await callApi<AddToCartResponse, ApiResponse<AddToCartResponse>>(
      {
        apiCall: () => {
          return cartApi.addItemToCart(
            {
              shopId,
              itemId,
              virtualSkuInfo,
              quantity,
              modelId,
              checkout,
              update_checkout_only,
              donot_add_quantity,
              source,
              add_on_deal_id,
              item_group_id,
              is_add_on_sub_item,
              client_source,
            },
            options
          );
        },
        actions: [
          {
            type: ADD_ITEM_TO_CART.REQUESTED,
          },
          {
            type: ADD_ITEM_TO_CART.SUCCESS,
            payload: (state, action, res) => {
              return {
                ...res,
                requestItem: {
                  shopId,
                  itemId,
                  quantity,
                  modelId,
                  checkout,
                  update_checkout_only,
                  donot_add_quantity,
                  source,
                  onSuccess,
                  done,
                  add_on_deal_id,
                  item_group_id,
                  is_add_on_sub_item,
                },
                showToastAfterSuccess,
              };
            },
          },
          {
            type: ADD_ITEM_TO_CART.FAILED,
            payload: (state, action, res) => {
              return {
                error: res.error,
                errorMessage: res.error_msg,
                usePopup: shouldUsePopupWhenAddToCartError(res.error),
              };
            },
          },
        ],
        onSuccess,
        onFinish: done,
      },
      dispatch,
      getState
    );
  };
}

export function addItemToCartBatch({
  items,
  onSuccess,
  done,
  shouldReset,
  clientSource,
  cart_client_id,
}: AddItemToCartBatchParams) {
  return async (dispatch: Dispatch, getState: GetState) => {
    const options = {
      shouldReset,
      clientSource,
      ...(cart_client_id ? { cart_client_id } : null),
    };
    return await callApi(
      {
        apiCall: () => {
          return cartApi.addItemToCartBatch(items, options);
        },
        actions: [
          {
            type: ADD_ITEM_TO_CART_BATCH.REQUESTED,
          },
          {
            type: ADD_ITEM_TO_CART_BATCH.SUCCESS,
            payload: (state, action, res) => {
              return { ...res, items };
            },
          },
          {
            type: ADD_ITEM_TO_CART_BATCH.FAILED,
            payload: (state, action, res) => {
              return {
                error: res.error,
                errorMessage: res.error_msg,
              };
            },
          },
        ],
        onSuccess,
        onFinish: done,
      },
      dispatch,
      getState
    );
  };
}

export function removeQuantity({
  shopId,
  itemId,
  virtualSkuInfo,
  quantity,
  modelId,
  checkout = true,
  update_checkout_only = false,
  donot_add_quantity = false,
  source = '',
  onSuccess,
  done,
}: AddItemToCartParams) {
  return async (dispatch: Dispatch, getState: GetState) => {
    return await callApi(
      {
        apiCall: () => {
          return cartApi.addItemToCart({
            shopId,
            itemId,
            virtualSkuInfo,
            quantity,
            modelId,
            checkout,
            update_checkout_only,
            donot_add_quantity,
            source,
          });
        },
        actions: [
          {
            type: REMOVE_QUANTITY_ITEM_FROM_CART.REQUESTED,
          },
          {
            type: REMOVE_QUANTITY_ITEM_FROM_CART.SUCCESS,
            payload: (state, action, res) => {
              return res;
            },
          },
          {
            type: REMOVE_QUANTITY_ITEM_FROM_CART.FAILED,
            payload: (state, action, res) => {
              return {
                error: res.error,
                errorMessage: res.error_msg,
              };
            },
          },
        ],
        onSuccess: () => {
          // refetch cart item count on success
          dispatch(fetchCartDrawerItems());
          onSuccess && onSuccess();
        },
        onFinish: done,
      },
      dispatch,
      getState
    );
  };
}
export type ActionAddItemToCart = ThunkAction<typeof addItemToCart>;

/**
 * Redux thunk action: request show add to cart panel
 */
export function requestShowAddToCartPanel(
  shopId: number,
  itemId: number,
  {
    forceShowCartPanel = false,
    getItemById = getItemByIdDefault,
  }: {
    forceShowCartPanel?: boolean;
    getItemById?: (
      state: Object,
      itemId: number
    ) => ItemDetail | undefined | null;
  } = {}
) {
  return async (dispatch: Dispatch) => {
    const item = await dispatch(
      fetchItemDetailCached(shopId, itemId, getItemById)
    );
    const models = item ? item.models : null;
    // SPFE-6914 If item has zero or only one variation, no need to show add to cart panel, except when "Select Variation" is clicked
    // SPFE-2724 If item is not wholesale and no variation, no need to show add to cart panel
    if (
      !forceShowCartPanel &&
      item &&
      !item.can_use_wholesale &&
      models &&
      ((models.length === 0 && !shouldShowMPQ(item)) ||
        // If there are exactly one model, to skip ATC panel, I also want it to be normal status and have stock
        (models.length === 1 &&
          models[0].stock > 0 &&
          models[0].status === ITEM_MODEL_STATUS.NORMAL &&
          /** Among valid single modal items, If it is MPQ item, will open ATC panel */
          !shouldShowMPQ(item)))
    ) {
      return {
        shouldShowAddToCartPanel: false,
      };
    }
    return {
      shouldShowAddToCartPanel: true,
    };
  };
}

/**
 * Redux thunk action: add to cart
 * @param {*} item
 */
export function addItemToCartWithToastMessages(item: AddItemToCartParams) {
  return async (dispatch: Dispatch) => {
    const result = await dispatch(addItemToCart(item));
    if (result && result.error) {
      const errorCode = result.error;
      const errorMessage = result.error_msg || 'msg_item_not_available';
      return {
        toastMessage: errorMessage,
        errorCode,
        usePopup: shouldUsePopupWhenAddToCartError(errorCode),
      };
    } else {
      // refetch cart item count on success
      dispatch(fetchCartDrawerItems());
      const cartItem = getIn(result, ['response', 'data', 'cart_item'], null);
      return {
        cartItem,
        toastMessage: 'msg_item_has_been_added_to_cart',
        errorCode: 0,
        usePopup: false,
      };
    }
  };
}

/**
 * Redux thunk action: add to cart batch with toast messages
 * @param {*} item, options
 */
export function addItemBatchToCartWithToastMessages({
  items,
  options,
}: {
  items: Array<AddToCartItem>;
  options?: {
    shouldReset?: boolean;
    onSuccess?: Function;
    done?: Function;
    cart_client_id?: CartClientId;
  };
}) {
  return async (dispatch: Dispatch) => {
    const { shouldReset, onSuccess, done } = options || {};
    const result = await dispatch(
      addItemToCartBatch({
        items,
        shouldReset,
        onSuccess: onSuccess as () => void,
        done: done as () => void,
      })
    );
    if (result && result.error) {
      const errorCode = result.error;
      const errorMessage = result.error_msg || 'msg_item_not_available';
      return {
        toastMessage: errorMessage,
        errorCode,
        data: result.data,
      };
    } else {
      // refetch cart item count on success
      dispatch(fetchCartDrawerItems());
      return {
        toastMessage: 'msg_item_has_been_added_to_cart',
        errorCode: 0,
        data: null,
      };
    }
  };
}
